import {useRef, useCallback} from 'react';
import {useField} from 'formik';
import ReactQuill from 'react-quill';
import TextEditor from './TextEditor';
import {convertTextBeforeSave} from './TextEditorHelpers';

interface TextEditorFormikProps {
    name: string;
    limit?: {maxChar?: number; showCharCounter?: boolean; showMessagesAmount?: boolean};
    dependencies?: unknown[];
    showToolbar?: boolean;
    expanded?: boolean;
    variablesType?: string;
    lang?: string;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    minHeight?: string | number;
    placeholder?: string;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    disableVariables?: boolean;
}

function TextEditorFormik({
    name,
    limit = {},
    dependencies = [],
    showToolbar = false,
    expanded = false,
    variablesType = '',
    lang,
    prefix = null,
    suffix = null,
    minHeight = null,
    placeholder = '',
    onKeyDown,
    disableVariables = false
}: TextEditorFormikProps) {
    const [field, , helper] = useField(name);
    const quillRef = useRef<ReactQuill | null>(null);

    const handleChange = useCallback(() => {
        const htmlValue = convertTextBeforeSave(quillRef);
        helper.setValue(htmlValue);
    }, [helper]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.stopPropagation();
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    }, [onKeyDown]);

    return (
        <div onKeyDown={handleKeyDown}>
            <TextEditor
                quillRef={quillRef}
                defaultText={field.value ?? ''}
                onChange={handleChange}
                onSave={handleChange}
                dependencies={dependencies}
                showToolbar={showToolbar}
                expanded={expanded}
                limit={limit}
                variablesType={variablesType}
                lang={lang}
                prefix={prefix}
                suffix={suffix}
                minHeight={minHeight}
                placeholder={placeholder}
                disableVariables={disableVariables}
            />
        </div>
    );
}

export default TextEditorFormik;
